import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import {
	GET_REWARDS_STORE_SETTINGS,
	UPDATE_REWARDS_STORE_SETTINGS,
	QUERY_REWARDS_STORE_REWARDS,
	QUERY_REWARDS_STORE_REWARDS_AMOUNT,
	QUERY_REWARDS_STORE_TRANSACTIONS_BY_COMPANY_ID,
	QUERY_REWARDS_STORE_TRANSACTIONS_BY_USER_ID,
	QUERY_USER_GROUPS_BY_COMPANY_ID,
	CREATE_REWARDS_STORE_AVAILABILITY_GROUP,
	CREATE_REWARDS_STORE_REWARD,
	CREATE_REWARDS_STORE_TRANSACTION,
	CREATE_REWARDS_STORE_TRANSACTION_SNAPPY,
	UPDATE_REWARDS_STORE_AVAILABILITY_GROUP,
	UPDATE_REWARDS_STORE_REWARD,
	UPDATE_REWARDS_STORE_TRANSACTION_STATUS,
	DELETE_REWARDS_STORE_AVAILABILITY_GROUP,
	DELETE_REWARDS_STORE_REWARD,
	CALL_SNAPPY_API,
	GET_AVAILABLE_POINTS,
} from 'src/rewardsStore/RewardsStoreGraphQL';
import SnappyApiTestModal from './SnappyApiTestModal';
import AddRewardModal from './AddRewardModal';
import { motion } from 'motion/react';
import { USER_ROLES } from 'src/_shared/constants';
import { countryOptions } from './options.js';
import snappyPresent from 'src/_shared/assets/snappy_present.png';
import { handlePrintCertificate } from './helpers.js';
import dayjs from 'dayjs';
import { QueryPointsBalancesByUserId } from 'src/_shared/api/graphql/custom/dashboard/queryPointsBalancesByUserIdGraphql.js';
import { GetPointsSettings } from 'src/new-points/pointsSettingsHelpers.js';

import {
	Card,
	Row,
	Col,
	Typography,
	Table,
	Tag,
	Statistic,
	Tabs,
	Spin,
	Empty,
	Divider,
	Button,
	Modal,
	Form,
	Input,
	InputNumber,
	Select,
	Switch,
	message,
	Space,
	Popconfirm,
	Badge,
	Image,
	Tooltip,
	Segmented,
} from 'antd';
import {
	PlusOutlined,
	EditOutlined,
	DeleteOutlined,
	ShoppingCartOutlined,
	GiftOutlined,
	CreditCardOutlined,
	StarOutlined,
	TrophyOutlined,
	HistoryOutlined,
	DollarOutlined,
	EnvironmentOutlined,
	SettingOutlined,
	BugOutlined,
	ExportOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const REWARD_TYPE_MAP = {
	gifts: 'gifts',
	giftCards: 'giftCards',
	nonMonetary: 'nonMonetary',
	swag: 'swag',
};

const AvailabilityGroupModal = ({
	editingAvailabilityGroup,
	onCancel,
	onFinish,
	userGroups,
	countryOptions,
}) => {
	const [form] = Form.useForm();

	useEffect(() => {
		if (editingAvailabilityGroup) {
			if (editingAvailabilityGroup.id) {
				// Editing existing group
				form.setFieldsValue({
					name: editingAvailabilityGroup.name,
					description: editingAvailabilityGroup.description,
					countryCodes: editingAvailabilityGroup.countryCodes || [],
					userGroupIds: editingAvailabilityGroup.userGroupIds,
				});
			} else {
				// Creating new group
				form.resetFields();
			}
		}
	}, [editingAvailabilityGroup, form]);

	return (
		<Modal
			title={
				editingAvailabilityGroup.id
					? 'Edit Availability Group'
					: 'Create Availability Group'
			}
			open={!!editingAvailabilityGroup}
			onCancel={onCancel}
			onOk={() => form.submit()}
			centered
			destroyOnClose
		>
			<Form form={form} layout="vertical" onFinish={onFinish}>
				<Form.Item
					name="name"
					label="Name"
					rules={[{ required: true, message: 'Please enter group name' }]}
				>
					<Input placeholder="Enter group name" />
				</Form.Item>

				<Form.Item
					name="description"
					label="Description"
					rules={[{ required: true, message: 'Please enter description' }]}
				>
					<Input.TextArea placeholder="Enter group description" rows={3} />
				</Form.Item>

				<Form.Item
					name="countryCodes"
					label="Countries"
					rules={[{ required: true, message: 'Please select countries' }]}
				>
					<Select
						mode="multiple"
						placeholder="Select countries"
						options={countryOptions}
					/>
				</Form.Item>

				<Form.Item
					name="userGroupIds"
					label="User Groups"
					rules={[{ required: true, message: 'Please select user groups' }]}
				>
					<Select
						mode="multiple"
						placeholder="Select user groups"
						options={userGroups.map((ug) => ({
							label: ug.name,
							value: ug.id,
						}))}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

const RewardsStoreComponent = (props) => {
	const currentUser = useSelector((state) => state.user.currentUser);
	const isAdmin =
		(currentUser?.role === USER_ROLES.ADMIN &&
			currentUser?.displayAs === 'admin') ||
		currentUser?.role === USER_ROLES.SUPER_ADMIN;
	const supportAdmin =
		typeof currentUser?.admin === 'string' && JSON.parse(currentUser.admin);
	const isDeveloper = supportAdmin?.permissions === '*';
	const userGroupId = currentUser?.userGroupId;
	const client = useApolloClient();
	const companyId = currentUser?.company?.id;
	const userId = currentUser?.id;

	const [loading, setLoading] = useState(true);
	const [sortLoading, setSortLoading] = useState(false);
	const [activeTab, setActiveTab] = useState('gifts');
	const [pointsEarned, setPointsEarned] = useState(0);
	const [sortBy, setSortBy] = useState('amount'); // 'amount' or 'name'
	const [sortOrder, setSortOrder] = useState('ASC'); // 'ASC' or 'DESC'

	const [giftsData, setGiftsData] = useState([]);
	const [giftCardsData, setGiftCardsData] = useState([]);
	const [nonMonetaryData, setNonMonetaryData] = useState([]);
	const [swagData, setSwagData] = useState([]);

	const [nextTokens, setNextTokens] = useState({
		gifts: null,
		giftCards: null,
		nonMonetary: null,
		swag: null,
	});
	const [loadingMore, setLoadingMore] = useState({
		gifts: false,
		giftCards: false,
		nonMonetary: false,
		swag: false,
	});

	const [rewardsStoreSettings, setRewardsStoreSettings] = useState({});
	const [tabSettings, setTabSettings] = useState({});
	const [availabilityGroups, setAvailabilityGroups] = useState([]);

	const [addRewardModal, setAddRewardModal] = useState(false);
	const [editRewardModal, setEditRewardModal] = useState(false);
	const [redeemModal, setRedeemModal] = useState(false);
	const [redeemLoading, setRedeemLoading] = useState(false);
	const [historyModal, setHistoryModal] = useState(false);
	const [settingsModal, setSettingsModal] = useState(false);
	const [snappyApiTestModal, setSnappyApiTestModal] = useState(false);
	const [selectedReward, setSelectedReward] = useState(null);
	const [editingReward, setEditingReward] = useState(null);

	const [editForm] = Form.useForm();
	const [settingsForm] = Form.useForm();

	const [checkoutHistory, setCheckoutHistory] = useState([]);
	const [adminTransactionHistory, setAdminTransactionHistory] = useState([]);
	const [userGroups, setUserGroups] = useState([]);
	const [campaignOptions, setCampaignOptions] = useState([]);
	const [editingAvailabilityGroup, setEditingAvailabilityGroup] =
		useState(null);
	const [adminHistoryModal, setAdminHistoryModal] = useState(false);

	useEffect(() => {
		if (companyId) {
			fetchStoreData();
		}
	}, [companyId]);

	const observerRef = useRef(null);

	const sentinelCallbackRef = useCallback(
		(node) => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}

			if (node && nextTokens[activeTab] && !loadingMore[activeTab]) {
				observerRef.current = new IntersectionObserver(
					(entries) => {
						if (entries[0].isIntersecting) {
							fetchRewardsByType(activeTab, nextTokens[activeTab], true);
						}
					},
					{
						root: null,
						rootMargin: '200px',
						threshold: 0.1,
					}
				);

				observerRef.current.observe(node);
			}
		},
		[activeTab, nextTokens, loadingMore]
	);

	useEffect(() => {
		// Overrides Ant D styles to make cards nice, keeping isolated from global rules until finalized
		const style = document.createElement('style');
		style.textContent = `
			.rewards-store-container .ant-card {
				border-radius: 14px !important;
				overflow: hidden !important;
			}
			.rewards-store-container .ant-card .ant-card-body {
				border-radius: 0 0 14px 14px !important;
			}
			.rewards-store-container .ant-card .ant-card-actions {
				border-radius: 0 0 14px 14px !important;
				border-top: 1px solid #f0f0f0 !important;
				padding: 12px !important;
			}
			.rewards-store-container .ant-card .ant-card-actions > li {
				margin: 0 !important;
				padding: 0 4px !important;
			}
			.rewards-store-container .ant-card .ant-card-actions > li:first-child {
				border-radius: 0 0 0 14px !important;
			}
			.rewards-store-container .ant-card .ant-card-actions > li:last-child {
				border-radius: 0 0 14px 0 !important;
			}
			.rewards-store-container .ant-card .ant-card-cover {
				border-radius: 14px 14px 0 0 !important;
				overflow: hidden !important;
			}
		`;
		document.head.appendChild(style);

		return () => {
			document.head.removeChild(style);
		};
	}, []);

	const newPointsEarlyPreview = currentUser?.featureFlags?.includes(
		'newPointsEarlyPreview'
	);

	const getCurrentPeriodKey = (primaryPeriod) => {
		const now = dayjs();
		switch (primaryPeriod) {
			case 'YEARLY':
				return now.format('YYYY');
			case 'QUARTERLY': {
				const quarter = Math.ceil((now.month() + 1) / 3);
				return `${now.format('YYYY')}-Q${quarter}`;
			}
			case 'MONTHLY':
				return now.format('YYYY-MM');
			case 'ALLTIME':
			default:
				return null;
		}
	};

	const fetchAvailablePoints = async () => {
		try {
			if (newPointsEarlyPreview) {
				const [balancesResponse, settingsResponse] = await Promise.all([
					client.query({
						query: QueryPointsBalancesByUserId,
						variables: { userId, first: 100 },
						fetchPolicy: 'network-only',
					}),
					client.query({
						query: GetPointsSettings,
						variables: { companyId },
						fetchPolicy: 'network-only',
					}),
				]);

				const balances = (
					balancesResponse?.data?.queryPointsBalancesByUserId?.items ?? []
				).filter((b) => b.companyId === companyId);
				const settings = settingsResponse?.data?.getPointsSettings;
				const primaryPeriod = settings?.primaryPeriod ?? 'ALLTIME';
				const excludeSpendablePointsFromReset =
					settings?.excludeSpendablePointsFromReset ?? false;

				let matchedBalance;
				if (primaryPeriod === 'ALLTIME' || excludeSpendablePointsFromReset) {
					matchedBalance = balances.find((b) => b.isAllTime);
				} else {
					const periodKey = getCurrentPeriodKey(primaryPeriod);
					matchedBalance = balances.find((b) => b.periodKey === periodKey);
				}

				setPointsEarned(matchedBalance?.currentSpendable ?? 0);
			} else {
				const { data } = await client.query({
					query: GET_AVAILABLE_POINTS,
					variables: { userId, companyId },
					fetchPolicy: 'network-only',
				});

				if (data?.getAvailablePoints !== undefined) {
					setPointsEarned(data.getAvailablePoints);
				} else {
					setPointsEarned(0);
				}
			}
		} catch (error) {
			setPointsEarned(0);
			console.error('Error fetching available points:', error);
		}
	};

	const fetchStoreData = async () => {
		try {
			setLoading(true);

			const { data: settingsData } = await client.query({
				query: GET_REWARDS_STORE_SETTINGS,
				variables: { companyId },
				fetchPolicy: 'network-only',
			});

			if (settingsData?.getRewardsStoreSettings) {
				const settings = settingsData.getRewardsStoreSettings;
				setRewardsStoreSettings(settings);
				setAvailabilityGroups(settings.availabilityGroups || []);

				const tabSettingsObj = {};
				if (settings.tabSettings) {
					settings.tabSettings.forEach((tab) => {
						tabSettingsObj[tab.rewardType] = {
							active: tab.active,
							name: tab.name,
						};
					});
				}
				setTabSettings(tabSettingsObj);
			}

			setNextTokens({
				gifts: null,
				giftCards: null,
				nonMonetary: null,
				swag: null,
			});
			setLoadingMore({
				gifts: false,
				giftCards: false,
				nonMonetary: false,
				swag: false,
			});

			await Promise.all([
				fetchRewardsByType('gifts'),
				fetchRewardsByType('giftCards'),
				fetchRewardsByType('nonMonetary'),
				fetchRewardsByType('swag'),
			]);

			if (userId) {
				const promises = [fetchAvailablePoints()];

				if (isAdmin && currentUser?.displayAs === 'admin') {
					promises.push(fetchAdminTransactionHistory());
				}

				await Promise.all(promises);
			}
		} catch (error) {
			console.error('Error fetching store data:', error);
			message.error('Failed to load store data');
		} finally {
			setLoading(false);
		}
	};

	const fetchRewardsByType = async (
		rewardType,
		nextToken = null,
		append = false,
		customSortBy = null,
		customSortOrder = null
	) => {
		try {
			if (nextToken) {
				setLoadingMore((prev) => ({ ...prev, [rewardType]: true }));
			}

			const activeSortBy = customSortBy !== null ? customSortBy : sortBy;
			const activeSortOrder =
				customSortOrder !== null ? customSortOrder : sortOrder;

			const query =
				activeSortBy === 'amount'
					? QUERY_REWARDS_STORE_REWARDS_AMOUNT
					: QUERY_REWARDS_STORE_REWARDS;

			const { data } = await client.query({
				query,
				variables: {
					companyId,
					rewardType: REWARD_TYPE_MAP[rewardType],
					sortOrder: activeSortOrder,
					limit: 20,
					nextToken,
				},
				fetchPolicy: 'network-only',
			});

			const responseKey =
				activeSortBy === 'amount'
					? 'queryRewardsStoreRewardsByCompanyIdRewardTypeAmount'
					: 'queryRewardsStoreRewardsByCompanyIdRewardType';

			if (data?.[responseKey]) {
				const response = data[responseKey];
				const rewards = response.items || [];
				const newNextToken = response.nextToken;

				setNextTokens((prev) => ({ ...prev, [rewardType]: newNextToken }));

				switch (rewardType) {
					case 'gifts':
						setGiftsData((prev) => (append ? [...prev, ...rewards] : rewards));
						break;
					case 'giftCards':
						setGiftCardsData((prev) =>
							append ? [...prev, ...rewards] : rewards
						);
						break;
					case 'nonMonetary':
						setNonMonetaryData((prev) =>
							append ? [...prev, ...rewards] : rewards
						);
						break;
					case 'swag':
						setSwagData((prev) => (append ? [...prev, ...rewards] : rewards));
						break;
				}
			}
		} catch (error) {
			console.error(`Error fetching ${rewardType} rewards:`, error);
		} finally {
			if (nextToken) {
				setLoadingMore((prev) => ({ ...prev, [rewardType]: false }));
			}
		}
	};

	const fetchTransactionHistory = async () => {
		try {
			const { data } = await client.query({
				query: QUERY_REWARDS_STORE_TRANSACTIONS_BY_USER_ID,
				variables: { userId, companyId },
				fetchPolicy: 'network-only',
			});

			if (data?.queryRewardsStoreTransactionsByUserId?.items) {
				setCheckoutHistory(data.queryRewardsStoreTransactionsByUserId.items);
			}
		} catch (error) {
			console.error('Error fetching transaction history:', error);
		}
	};

	const fetchAdminTransactionHistory = async () => {
		if (!isAdmin) return;

		try {
			const { data } = await client.query({
				query: QUERY_REWARDS_STORE_TRANSACTIONS_BY_COMPANY_ID,
				variables: { companyId },
				fetchPolicy: 'network-only',
			});

			if (data?.queryRewardsStoreTransactionsByCompanyId?.items) {
				setAdminTransactionHistory(
					data.queryRewardsStoreTransactionsByCompanyId.items
				);
			}
		} catch (error) {
			console.error('Error fetching admin transaction history:', error);
		}
	};

	const getCurrentTabData = () => {
		switch (activeTab) {
			case 'gifts':
				return giftsData;
			case 'giftCards':
				return giftCardsData;
			case 'nonMonetary':
				return nonMonetaryData;
			case 'swag':
				return swagData;
			default:
				return [];
		}
	};

	const getFilteredRewards = (data) => {
		if (!isAdmin) {
			return data.filter((reward) => {
				if (!reward.active) return false;

				if (
					reward.availabilityGroups &&
					reward.availabilityGroups.length > 0 &&
					userGroupId
				) {
					return reward.availabilityGroups.some(
						(group) =>
							group.userGroupIds && group.userGroupIds.includes(userGroupId)
					);
				}

				return true;
			});
		}
		return data;
	};

	const handleAddReward = async (values) => {
		try {
			const { data } = await client.mutate({
				mutation: CREATE_REWARDS_STORE_REWARD,
				variables: {
					input: values?.externalId
						? values
						: {
								companyId,
								name: values.name,
								description: values.description,
								amount: values.amount,
								availabilityGroupIds: values.availabilityGroupIds,
								image: values.image,
								active: values.active ?? true,
								rewardType: REWARD_TYPE_MAP[values.rewardType],
								externalId: 'no_external',
							},
				},
			});

			if (data?.createRewardsStoreReward) {
				await fetchRewardsByType(values.rewardType);
				setAddRewardModal(false);
				message.success('Reward added successfully!');
			}
		} catch (error) {
			console.error('Error adding reward:', error);
			message.error('Failed to add reward');
		}
	};

	const handleEditReward = async (values) => {
		try {
			const { data } = await client.mutate({
				mutation: UPDATE_REWARDS_STORE_REWARD,
				variables: {
					input: {
						id: editingReward.id,
						companyId,
						name: values.name,
						description: values.description,
						amount: values.amount,
						availabilityGroupIds: values.availabilityGroupIds,
						image: values.image,
						active: values.active,
						rewardType: REWARD_TYPE_MAP[activeTab],
						externalId: editingReward.externalId || 'no_external',
					},
				},
			});

			if (data?.updateRewardsStoreReward) {
				await fetchRewardsByType(activeTab);
				setEditRewardModal(false);
				setEditingReward(null);
				editForm.resetFields();
				message.success('Reward updated successfully!');
			}
		} catch (error) {
			console.error('Error updating reward:', error);
			message.error('Failed to update reward');
		}
	};

	const handleDeleteReward = async (rewardId) => {
		try {
			const { data } = await client.mutate({
				mutation: DELETE_REWARDS_STORE_REWARD,
				variables: {
					companyId,
					id: rewardId,
				},
			});

			if (data?.deleteRewardsStoreReward) {
				await fetchRewardsByType(activeTab);
				message.success('Reward deleted successfully!');
			}
		} catch (error) {
			console.error('Error deleting reward:', error);
			message.error('Failed to delete reward');
		}
	};

	const handleToggleActive = async (rewardId, isActive) => {
		const reward = getCurrentTabData().find((r) => r.id === rewardId);
		if (!reward) return;

		try {
			const { data } = await client.mutate({
				mutation: UPDATE_REWARDS_STORE_REWARD,
				variables: {
					input: {
						id: rewardId,
						companyId,
						name: reward.name,
						description: reward.description,
						amount: reward.amount,
						availabilityGroupIds: reward.availabilityGroupIds,
						image: reward.image,
						active: isActive,
						rewardType: reward.rewardType,
						externalId: reward.externalId || 'no_external',
					},
				},
			});

			if (data?.updateRewardsStoreReward) {
				await fetchRewardsByType(activeTab);
				message.success(
					`Reward ${isActive ? 'activated' : 'deactivated'} successfully!`
				);
			}
		} catch (error) {
			console.error('Error toggling reward status:', error);
			message.error('Failed to update reward status');
		}
	};

	const handleRedeemReward = async () => {
		if (!selectedReward) return;

		try {
			setRedeemLoading(true);

			const useSnappyMutation = selectedReward?.snappyProduct?.id;

			let data;
			if (useSnappyMutation) {
				// Use snappy rewards mutation
				const input = {
					companyId: currentUser.companyId,
					userId: currentUser.id,
					campaignId: rewardsStoreSettings.campaignId,
					productId: selectedReward.snappyProduct.id,
					rewardId: selectedReward.id,
					amount: selectedReward.amount,
					budget: selectedReward.snappyProduct.budget,
					firstName: currentUser.firstName,
					lastName: currentUser.lastName,
					email: currentUser.emailAddress,
					phone: '',
				};

				const response = await client.mutate({
					mutation: CREATE_REWARDS_STORE_TRANSACTION_SNAPPY,
					variables: { input },
				});
				data = response.data;
			} else {
				// Use manual rewards mutation
				const response = await client.mutate({
					mutation: CREATE_REWARDS_STORE_TRANSACTION,
					variables: {
						input: {
							companyId,
							userId,
							rewardId: selectedReward.id,
							amount: selectedReward.amount,
						},
					},
				});
				data = response.data;
			}

			const transaction = useSnappyMutation
				? data?.createRewardsStoreTransactionSnappy
				: data?.createRewardsStoreTransaction;

			if (transaction) {
				const promises = [fetchAvailablePoints()];

				if (isAdmin) {
					promises.push(fetchAdminTransactionHistory());
				} else {
					promises.push(fetchTransactionHistory());
				}

				await Promise.all(promises);
				setRedeemModal(false);
				setSelectedReward(null);

				const remainingPoints = pointsEarned - selectedReward.amount;
				let successMessage = 'Successfully redeemed reward!';

				if (useSnappyMutation && transaction.link) {
					message.success({
						content: (
							<div>
								<div>{successMessage}</div>
								<div>
									<strong>Reward Name:</strong> {selectedReward.name}
								</div>
								<div>
									<strong>Amount:</strong> {selectedReward.amount} points
								</div>
								<div>
									<strong>Remaining Points:</strong> {remainingPoints}
								</div>
								<div>
									<a
										href={transaction.link}
										target="_blank"
										rel="noopener noreferrer"
									>
										Click here to access your reward
									</a>
								</div>
							</div>
						),
						duration: 10,
					});
				} else {
					message.success(successMessage);
				}
			} else {
				message.error('Something went wrong');
			}
		} catch (error) {
			console.error('Error redeeming reward:', error);
			message.error('Something went wrong');
		} finally {
			setRedeemLoading(false);
		}
	};

	const openEditModal = (reward) => {
		setEditingReward(reward);
		const availabilityGroupIds =
			reward.availabilityGroupIds ||
			(reward.availabilityGroups
				? reward.availabilityGroups.map((ag) => ag.id)
				: []);

		editForm.setFieldsValue({
			name: reward.name,
			description: reward.description,
			amount: reward.amount,
			availabilityGroupIds: availabilityGroupIds,
			image: reward.image,
			active: reward.active,
		});
		setEditRewardModal(true);
	};

	const openRedeemModal = (reward) => {
		setSelectedReward(reward);
		setRedeemModal(true);
	};

	const calculatePointsToCurrency = (points) => {
		const pointsToCurrencyRatio =
			rewardsStoreSettings?.pointsToCurrencyRatio ?? 100;
		return points / pointsToCurrencyRatio;
	};

	const fetchUserGroups = async () => {
		try {
			const { data } = await client.query({
				query: QUERY_USER_GROUPS_BY_COMPANY_ID,
				variables: { companyId },
				fetchPolicy: 'network-only',
			});

			if (data?.queryUserGroupsByCompanyIdIndex?.items) {
				setUserGroups(data.queryUserGroupsByCompanyIdIndex.items);
			}
		} catch (error) {
			console.error('Error fetching user groups:', error);
		}
	};

	const fetchCampaigns = async () => {
		try {
			const { data } = await client.query({
				query: CALL_SNAPPY_API,
				variables: {
					companyId: currentUser.companyId,
					endpoint: 'campaigns',
					method: 'GET',
					queryParams: [
						{ key: 'types', value: 'oneOffs' },
						{ key: 'fields', value: 'id,name,properties' },
					],
				},
				fetchPolicy: 'network-only',
			});

			if (data?.callSnappyApi) {
				try {
					const parsedResponse = JSON.parse(data.callSnappyApi);
					const results = parsedResponse?.results || [];
					if (results && Array.isArray(results)) {
						const campaignOptions = results
							// This filters the campaigns that are collection based rather then product based
							.filter(
								(campaign) => campaign?.properties?.[0]?.collection?.id === null
							)
							.map((result) => ({
								label: result.name,
								value: result.id,
							}));
						setCampaignOptions(campaignOptions);
					} else {
						setCampaignOptions([]);
					}
				} catch (parseError) {
					console.error('Error parsing campaigns response:', parseError);
					setCampaignOptions([]);
				}
			} else {
				setCampaignOptions([]);
			}
		} catch (error) {
			console.error('Error fetching campaigns:', error);
			setCampaignOptions([]);
		}
	};

	const openSettingsModal = async () => {
		await Promise.all([fetchUserGroups(), fetchCampaigns()]);

		const defaultTabSettings = [
			{
				active: true,
				name: 'Gifts',
				rewardType: 'gifts',
			},
			{
				active: true,
				name: 'Gift Cards',
				rewardType: 'giftCards',
			},
			{
				active: true,
				name: 'Non-Monetary',
				rewardType: 'nonMonetary',
			},
			{
				active: true,
				name: 'Swag',
				rewardType: 'swag',
			},
		];

		const tabSettingsArray =
			Object.keys(tabSettings).length > 0
				? Object.keys(tabSettings).map((key) => ({
						rewardType: key,
						active: tabSettings[key].active,
						name: tabSettings[key].name,
					}))
				: defaultTabSettings;

		settingsForm.setFieldsValue({
			active: rewardsStoreSettings.active ?? true,
			pointsToCurrencyRatio: rewardsStoreSettings.pointsToCurrencyRatio ?? 100,
			campaignId: rewardsStoreSettings.campaignId,
			tabSettings: tabSettingsArray,
		});

		setSettingsModal(true);
	};

	const handleSettingsSubmit = async (values) => {
		try {
			const { data } = await client.mutate({
				mutation: UPDATE_REWARDS_STORE_SETTINGS,
				variables: {
					input: {
						companyId,
						active: values.active,
						pointsToCurrencyRatio: values.pointsToCurrencyRatio,
						campaignId: values.campaignId,
						tabSettings: values.tabSettings,
					},
				},
			});

			if (data?.updateRewardsStoreSettings) {
				await fetchStoreData();
				setSettingsModal(false);
				settingsForm.resetFields();
				message.success('Settings updated successfully!');
			}
		} catch (error) {
			console.error('Error updating settings:', error);
			message.error('Failed to update settings');
		}
	};

	const handleCreateAvailabilityGroup = async (values) => {
		try {
			const { data } = await client.mutate({
				mutation: CREATE_REWARDS_STORE_AVAILABILITY_GROUP,
				variables: {
					input: {
						companyId,
						name: values.name,
						description: values.description,
						countryCodes: values.countryCodes || [],
						userGroupIds: values.userGroupIds,
					},
				},
			});

			if (data?.createRewardsStoreAvailabilityGroup) {
				await fetchStoreData();
				setEditingAvailabilityGroup(null);
				message.success('Availability group created successfully!');
			}
		} catch (error) {
			console.error('Error creating availability group:', error);
			message.error('Failed to create availability group');
		}
	};

	const handleUpdateAvailabilityGroup = async (values) => {
		try {
			const { data } = await client.mutate({
				mutation: UPDATE_REWARDS_STORE_AVAILABILITY_GROUP,
				variables: {
					input: {
						id: editingAvailabilityGroup.id,
						companyId,
						name: values.name,
						description: values.description,
						countryCodes: values.countryCodes || [],
						userGroupIds: values.userGroupIds,
					},
				},
			});

			if (data?.updateRewardsStoreAvailabilityGroup) {
				await fetchStoreData();
				setEditingAvailabilityGroup(null);
				message.success('Availability group updated successfully!');
			}
		} catch (error) {
			console.error('Error updating availability group:', error);
			message.error('Failed to update availability group');
		}
	};

	const handleDeleteAvailabilityGroup = async (groupId) => {
		try {
			const { data } = await client.mutate({
				mutation: DELETE_REWARDS_STORE_AVAILABILITY_GROUP,
				variables: {
					companyId,
					id: groupId,
				},
			});

			if (data?.deleteRewardsStoreAvailabilityGroup) {
				await fetchStoreData();
				message.success('Availability group deleted successfully!');
			}
		} catch (error) {
			console.error('Error deleting availability group:', error);
			message.error('Failed to delete availability group');
		}
	};

	const openAvailabilityGroupModal = (group = null) => {
		setEditingAvailabilityGroup(
			group || { name: '', description: '', userGroupIds: [], countryCodes: [] }
		);
	};

	const handleSortChange = async (newSortBy, newSortOrder) => {
		setSortLoading(true);

		setGiftsData([]);
		setGiftCardsData([]);
		setNonMonetaryData([]);
		setSwagData([]);
		setNextTokens({
			gifts: null,
			giftCards: null,
			nonMonetary: null,
			swag: null,
		});

		if (newSortBy !== undefined) setSortBy(newSortBy);
		if (newSortOrder !== undefined) setSortOrder(newSortOrder);

		try {
			await Promise.all([
				fetchRewardsByType('gifts', null, false, newSortBy, newSortOrder),
				fetchRewardsByType('giftCards', null, false, newSortBy, newSortOrder),
				fetchRewardsByType('nonMonetary', null, false, newSortBy, newSortOrder),
				fetchRewardsByType('swag', null, false, newSortBy, newSortOrder),
			]);
		} finally {
			setSortLoading(false);
		}
	};

	const exportTransactionsToCSV = () => {
		try {
			const headers = [
				'Date Created',
				'Employee Name',
				'Reward Name',
				'Amount (points)',
				'Status',
			];

			const rows = adminTransactionHistory.map((transaction) => {
				const dateCreated = transaction.dateCreated
					? new Date(transaction.dateCreated).toLocaleDateString('en-US', {
							year: 'numeric',
							month: 'short',
							day: 'numeric',
							hour: '2-digit',
							minute: '2-digit',
						})
					: 'N/A';

				const employeeName = transaction.user
					? `${transaction.user.firstName} ${transaction.user.lastName}`
					: 'Unknown User';

				const rewardName = transaction.reward?.name || 'Unknown Reward';
				const amount = transaction.amount || 0;
				const status = transaction.status?.toUpperCase() || 'N/A';

				return [dateCreated, employeeName, rewardName, amount, status];
			});

			const csvContent = [
				headers.join(','),
				...rows.map((row) =>
					row
						.map((cell) => {
							const cellStr = String(cell);
							if (
								cellStr.includes(',') ||
								cellStr.includes('"') ||
								cellStr.includes('\n')
							) {
								return `"${cellStr.replace(/"/g, '""')}"`;
							}
							return cellStr;
						})
						.join(',')
				),
			].join('\n');

			const timestamp = new Date()
				.toISOString()
				.replace(/[:.]/g, '-')
				.slice(0, -5);

			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const link = document.createElement('a');
			if (link.download !== undefined) {
				const url = URL.createObjectURL(blob);
				link.setAttribute('href', url);
				link.setAttribute(
					'download',
					`rewards-store-transactions-${timestamp}.csv`
				);
				link.style.visibility = 'hidden';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			}

			message.success('Transactions exported successfully!');
		} catch (error) {
			console.error('Error exporting transactions:', error);
			message.error('Failed to export transactions');
		}
	};

	const getTabItems = () => {
		const allTabs = [
			{
				key: 'gifts',
				label: (
					<span>
						<GiftOutlined />
						{tabSettings?.gifts?.name || 'Gifts'}
						{isAdmin && (
							<Badge
								status={tabSettings.gifts?.active ? 'success' : 'default'}
								style={{ marginLeft: 8 }}
							/>
						)}
					</span>
				),
				children: renderTabContent('gifts'),
			},
			{
				key: 'giftCards',
				label: (
					<span>
						<CreditCardOutlined />
						{tabSettings?.giftCards?.name || 'Gift Cards'}
						{isAdmin && (
							<Badge
								status={tabSettings.giftCards?.active ? 'success' : 'default'}
								style={{ marginLeft: 8 }}
							/>
						)}
					</span>
				),
				children: renderTabContent('giftCards'),
			},
			{
				key: 'nonMonetary',
				label: (
					<span>
						<StarOutlined />
						{tabSettings?.nonMonetary?.name || 'Non-Monetary'}
						{isAdmin && (
							<Badge
								status={tabSettings.nonMonetary?.active ? 'success' : 'default'}
								style={{ marginLeft: 8 }}
							/>
						)}
					</span>
				),
				children: renderTabContent('nonMonetary'),
			},
			{
				key: 'swag',
				label: (
					<span>
						<TrophyOutlined />
						{tabSettings?.swag?.name || 'Swag'}
						{isAdmin && (
							<Badge
								status={tabSettings.swag?.active ? 'success' : 'default'}
								style={{ marginLeft: 8 }}
							/>
						)}
					</span>
				),
				children: renderTabContent('swag'),
			},
		];

		if (isAdmin) {
			return allTabs;
		} else {
			return allTabs.filter((tab) => tabSettings[tab.key]?.active);
		}
	};

	const renderTabContent = (tabKey) => {
		const data = getCurrentTabData();
		const filteredData = getFilteredRewards(data);

		return (
			<motion.div
				initial={{ opacity: 0, x: 20 }}
				animate={{ opacity: 1, x: 0 }}
				exit={{ opacity: 0, x: -20 }}
				transition={{ duration: 0.3, ease: 'easeOut' }}
			>
				{sortLoading ? (
					<div style={{ textAlign: 'center', padding: '50px' }}>
						<Spin size="large" />
					</div>
				) : filteredData.length === 0 ? (
					<Empty description="No rewards available" />
				) : (
					<Row gutter={[16, 16]}>
						{filteredData.map((reward) => (
							<Col xs={24} sm={12} md={8} lg={6} key={reward.id}>
								<Card
									style={{ borderRadius: 14, overflow: 'hidden' }}
									hoverable
									cover={
										<div style={{ height: 320, overflow: 'hidden' }}>
											<Image
												src={reward.image || snappyPresent}
												alt={reward.name}
												style={{
													width: '100%',
													height: '100%',
													objectFit: 'cover',
												}}
												fallback="/assets/rewards/placeholder.png"
											/>
										</div>
									}
									actions={
										isAdmin
											? [
													<Switch
														key="active"
														checked={reward.active}
														onChange={(checked) =>
															handleToggleActive(reward.id, checked)
														}
														checkedChildren="Active"
														unCheckedChildren="Inactive"
														size="small"
													/>,
													<EditOutlined
														key="edit"
														onClick={() => openEditModal(reward)}
													/>,
													<Popconfirm
														title="Are you sure you want to delete this reward?"
														onConfirm={() => handleDeleteReward(reward.id)}
														okText="Yes"
														cancelText="No"
													>
														<DeleteOutlined key="delete" />
													</Popconfirm>,
												]
											: [
													reward.amount && pointsEarned < reward.amount ? (
														<Tooltip
															key="redeem"
															title="Keep earning to unlock."
														>
															<Button
																type="primary"
																icon={<ShoppingCartOutlined />}
																disabled={true}
															>
																Claim
															</Button>
														</Tooltip>
													) : (
														<Button
															key="redeem"
															type="primary"
															icon={<ShoppingCartOutlined />}
															onClick={() => openRedeemModal(reward)}
														>
															Claim
														</Button>
													),
												]
									}
								>
									<Card.Meta
										title={
											<div
												style={{
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
												}}
											>
												<span>{reward.name}</span>
												{isAdmin && (
													<Badge
														status={reward.active ? 'success' : 'default'}
														text={reward.active ? 'Active' : 'Inactive'}
													/>
												)}
											</div>
										}
										description={
											<div>
												<div
													style={{
														fontSize: '14px',
														lineHeight: '1.6',
														maxHeight: '100px',
														overflowY: 'auto',
													}}
													className="rewards-store-description"
													dangerouslySetInnerHTML={{
														__html:
															reward.description
																?.replace('<p><p>', '')
																?.replace('</p></p>', '') ||
															'No description available',
													}}
												/>
												<div style={{ marginTop: 8 }}>
													{isAdmin ? (
														<Tag color="green">
															<DollarOutlined /> $
															{calculatePointsToCurrency(reward.amount)}
														</Tag>
													) : null}
													{reward.amount && (
														<Tag color="blue">
															<StarOutlined /> {reward.amount} points
														</Tag>
													)}
												</div>
												{isAdmin &&
													reward.availabilityGroups &&
													reward.availabilityGroups.length > 0 && (
														<div style={{ marginTop: 4 }}>
															<Tag color="orange">
																<EnvironmentOutlined />{' '}
																{reward.availabilityGroups
																	.map((ag) => {
																		return ag?.name || '';
																	})
																	.join(', ')}
															</Tag>
														</div>
													)}
											</div>
										}
									/>
								</Card>
							</Col>
						))}
						{tabKey === activeTab && nextTokens[activeTab] && (
							<div
								ref={sentinelCallbackRef}
								style={{
									height: '20px',
									width: '100%',
									backgroundColor: 'transparent',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									margin: '20px 0',
								}}
							></div>
						)}
					</Row>
				)}
			</motion.div>
		);
	};

	return (
		<div className="rewards-store-container">
			<Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
				<Col>
					<Title level={2}>Rewards Store</Title>
				</Col>
				<Col>
					<Space>
						{isAdmin && (
							<>
								<Button
									type="primary"
									icon={<PlusOutlined />}
									onClick={() => setAddRewardModal(true)}
								>
									Add Reward
								</Button>
								<Button
									icon={<HistoryOutlined />}
									onClick={() => setAdminHistoryModal(true)}
								>
									All Transactions
								</Button>
								{isDeveloper && (
									<Button
										icon={<BugOutlined />}
										onClick={() => setSnappyApiTestModal(true)}
									>
										Test Snappy API
									</Button>
								)}
								<Button icon={<SettingOutlined />} onClick={openSettingsModal}>
									Settings
								</Button>
							</>
						)}
						{(!isAdmin || currentUser?.displayAs !== 'admin') && (
							<Button
								icon={<HistoryOutlined />}
								onClick={async () => {
									setHistoryModal(true);
									await fetchTransactionHistory();
								}}
							>
								Purchase History
							</Button>
						)}
						{!isAdmin && pointsEarned > 0 && (
							<Card size="small">
								<Statistic
									title="Available Points"
									value={pointsEarned}
									prefix={<StarOutlined />}
								/>
							</Card>
						)}
					</Space>
				</Col>
			</Row>

			{loading ? (
				<div style={{ textAlign: 'center', padding: '50px' }}>
					<Spin size="large" />
				</div>
			) : (
				<Tabs
					activeKey={activeTab}
					onChange={setActiveTab}
					items={getTabItems()}
					size="large"
					tabBarExtraContent={
						<Space.Compact>
							<div
								className="rewards-store-sort-options"
								style={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<span style={{ fontWeight: 900, marginRight: 6 }}>
									Sort By:
								</span>
								<Segmented
									value={sortBy}
									onChange={(value) => handleSortChange(value, sortOrder)}
									options={[
										{ label: 'Points', value: 'amount' },
										{ label: 'Name', value: 'name' },
									]}
								/>
								<span
									style={{ fontWeight: 900, marginLeft: 6, marginRight: 6 }}
								>
									Order:
								</span>
								<Segmented
									value={sortOrder}
									onChange={(value) => handleSortChange(sortBy, value)}
									options={[
										{
											label: 'Asc',
											value: 'ASC',
											title: 'Ascending',
										},
										{
											label: 'Desc',
											value: 'DESC',
											title: 'Descending',
										},
									]}
								/>
							</div>
						</Space.Compact>
					}
				/>
			)}

			{/* Add Reward Modal */}
			<AddRewardModal
				open={addRewardModal}
				onCancel={() => setAddRewardModal(false)}
				onSubmit={handleAddReward}
				availabilityGroups={availabilityGroups}
				tabSettings={tabSettings}
				currentUser={currentUser}
				client={client}
				loading={loading}
				rewardsStoreSettings={rewardsStoreSettings}
			/>

			{/* Edit Reward Modal */}
			<Modal
				title="Edit Reward"
				open={editRewardModal}
				onCancel={() => {
					setEditRewardModal(false);
					setEditingReward(null);
					editForm.resetFields();
				}}
				onOk={() => editForm.submit()}
				width={600}
			>
				<Form form={editForm} layout="vertical" onFinish={handleEditReward}>
					<Form.Item
						name="name"
						label="Reward Name"
						rules={[{ required: true, message: 'Please enter reward name' }]}
					>
						<Input placeholder="Enter reward name" />
					</Form.Item>

					<Form.Item
						name="description"
						label="Description"
						rules={[{ required: true, message: 'Please enter description' }]}
					>
						<Input.TextArea rows={3} placeholder="Enter reward description" />
					</Form.Item>
					<Form.Item
						name="amount"
						label="Points Amount"
						rules={[{ required: true, message: 'Please enter points amount' }]}
					>
						<InputNumber
							min={0}
							placeholder="Enter points amount"
							style={{ width: '100%' }}
						/>
					</Form.Item>

					<Form.Item name="availabilityGroupIds" label="Availability Groups">
						<Select
							allowClear
							mode="multiple"
							placeholder="Select availability groups"
							options={availabilityGroups.map((ag) => {
								return { label: ag.name, value: ag.id };
							})}
						/>
					</Form.Item>

					<Form.Item name="image" label="Image URL">
						<Input placeholder="Enter image URL" />
					</Form.Item>

					<Form.Item name="active" label="Status" valuePropName="checked">
						<Switch checkedChildren="Active" unCheckedChildren="Inactive" />
					</Form.Item>
				</Form>
			</Modal>

			{/* Redeem Confirmation Modal */}
			<Modal
				title="Confirm Redemption"
				open={redeemModal}
				onCancel={() => {
					setRedeemModal(false);
					setSelectedReward(null);
				}}
				onOk={handleRedeemReward}
				okText="Confirm Redemption"
				cancelText="Cancel"
				okButtonProps={{
					disabled:
						(selectedReward && pointsEarned < selectedReward.amount) ||
						redeemLoading,
					loading: redeemLoading,
				}}
				cancelButtonProps={{
					disabled: redeemLoading,
				}}
			>
				{redeemLoading ? (
					<div style={{ textAlign: 'center', padding: '50px' }}>
						<Spin size="large" />
						<div style={{ marginTop: 16 }}>
							<Text>Processing your redemption...</Text>
						</div>
					</div>
				) : (
					selectedReward && (
						<div>
							<Title level={4}>{selectedReward.name}</Title>
							<div
								style={{
									fontSize: '14px',
									lineHeight: '1.6',
									maxHeight: '200px',
									overflowY: 'auto',
									border: '1px solid #f0f0f0',
									borderRadius: '6px',
									padding: '12px',
									backgroundColor: '#fafafa',
								}}
								className="rewards-store-description"
								dangerouslySetInnerHTML={{
									__html:
										selectedReward.description
											?.replace('<p><p>', '')
											?.replace('</p></p>', '') || 'No description available',
								}}
							/>
							<Divider />

							<Row gutter={16}>
								<Col span={12}>
									<Statistic
										title="Cost"
										value={selectedReward.amount}
										suffix="points"
									/>
								</Col>
								<Col span={12}>
									<Statistic
										title="Your Balance"
										value={pointsEarned}
										suffix="points"
									/>
								</Col>
							</Row>
							{pointsEarned < selectedReward.amount && (
								<div style={{ marginTop: 16 }}>
									<Text type="danger">
										Insufficient points. You need{' '}
										{selectedReward.amount - pointsEarned} more points.
									</Text>
								</div>
							)}
						</div>
					)
				)}
			</Modal>

			{/* Purchase History Modal */}
			<Modal
				title="Purchase History"
				open={historyModal}
				onCancel={() => setHistoryModal(false)}
				footer={null}
				width={800}
			>
				<Table
					dataSource={checkoutHistory}
					rowKey="id"
					columns={[
						{
							title: 'Date Redeemed',
							dataIndex: 'dateCreated',
							key: 'dateCreated',
							render: (dateCreated) => {
								if (!dateCreated) return 'N/A';
								const date = new Date(dateCreated);
								return date.toLocaleDateString('en-US', {
									year: 'numeric',
									month: 'short',
									day: 'numeric',
									hour: '2-digit',
									minute: '2-digit',
								});
							},
							sorter: (a, b) =>
								new Date(a.dateCreated) - new Date(b.dateCreated),
							defaultSortOrder: 'descend',
						},
						{
							title: 'Reward Name',
							dataIndex: 'reward',
							key: 'reward',
							render: (reward) => reward?.name || 'Unknown Reward',
						},
						{
							title: 'Amount',
							dataIndex: 'amount',
							key: 'amount',
							render: (amount) => `${amount} points`,
						},
						{
							title: 'Status',
							dataIndex: 'status',
							key: 'status',
							render: (status) => (
								<Tag
									color={
										status === 'completed'
											? 'green'
											: status === 'pending'
												? 'orange'
												: 'red'
									}
								>
									{status?.toUpperCase()}
								</Tag>
							),
						},
						{
							title: 'Claim',
							key: 'claim',
							render: (_, record) => {
								if (record.link) {
									return (
										<a
											href={record.link}
											target="_blank"
											rel="noopener noreferrer"
										>
											Link
										</a>
									);
								}
								return (
									<Button
										type="link"
										onClick={() => handlePrintCertificate(record)}
									>
										Print
									</Button>
								);
							},
						},
					]}
					pagination={{ pageSize: 10 }}
				/>
			</Modal>

			{/* Admin Transaction History Modal */}
			{isAdmin && (
				<Modal
					title="All Company Transactions"
					open={adminHistoryModal}
					onCancel={() => setAdminHistoryModal(false)}
					footer={
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							<Button
								type="primary"
								icon={<ExportOutlined />}
								onClick={exportTransactionsToCSV}
								disabled={
									!adminTransactionHistory ||
									adminTransactionHistory.length === 0
								}
							>
								Export to CSV
							</Button>
							<Button onClick={() => setAdminHistoryModal(false)}>Close</Button>
						</div>
					}
					width={1000}
				>
					<Table
						dataSource={adminTransactionHistory}
						rowKey="id"
						columns={[
							{
								title: 'Date Created',
								dataIndex: 'dateCreated',
								key: 'dateCreated',
								render: (dateCreated) => {
									if (!dateCreated) return 'N/A';
									const date = new Date(dateCreated);
									return date.toLocaleDateString('en-US', {
										year: 'numeric',
										month: 'short',
										day: 'numeric',
										hour: '2-digit',
										minute: '2-digit',
									});
								},
								sorter: (a, b) =>
									new Date(a.dateCreated) - new Date(b.dateCreated),
								defaultSortOrder: 'descend',
							},
							{
								title: 'Employee',
								dataIndex: 'user',
								key: 'user',
								render: (user) =>
									user ? `${user.firstName} ${user.lastName}` : 'Unknown User',
							},
							{
								title: 'Reward Name',
								dataIndex: 'reward',
								key: 'reward',
								render: (reward) => reward?.name || 'Unknown Reward',
							},
							{
								title: 'Amount',
								dataIndex: 'amount',
								key: 'amount',
								render: (amount) => `${amount} points`,
								sorter: (a, b) => a.amount - b.amount,
							},
							{
								title: 'Status',
								dataIndex: 'status',
								key: 'status',
								render: (status) => (
									<Tag
										color={
											status === 'completed'
												? 'green'
												: status === 'pending'
													? 'orange'
													: 'red'
										}
									>
										{status?.toUpperCase()}
									</Tag>
								),
							},
						]}
						pagination={{
							pageSize: 15,
							showSizeChanger: true,
							showTotal: (total, range) =>
								`${range[0]}-${range[1]} of ${total} transactions`,
						}}
						scroll={{ x: 800 }}
					/>
				</Modal>
			)}

			{/* Settings Modal */}
			<Modal
				title="Rewards Store Settings"
				open={settingsModal}
				onCancel={() => {
					setSettingsModal(false);
					settingsForm.resetFields();
					setEditingAvailabilityGroup(null);
				}}
				onOk={() => settingsForm.submit()}
				width={800}
				destroyOnClose
			>
				<Form
					form={settingsForm}
					layout="vertical"
					onFinish={handleSettingsSubmit}
				>
					<Row gutter={16}>
						<Col span={8}>
							<Form.Item
								name="active"
								label="Store Active"
								valuePropName="checked"
							>
								<Switch checkedChildren="Active" unCheckedChildren="Inactive" />
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="pointsToCurrencyRatio"
								label="Points to Currency Ratio"
								rules={[{ required: true, message: 'Please enter ratio' }]}
							>
								<InputNumber
									min={1}
									placeholder="e.g., 100 points = $1"
									style={{ width: '100%' }}
								/>
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item name="campaignId" label="Campaign">
								<Select
									allowClear
									placeholder="Select campaign"
									options={campaignOptions}
									style={{ width: '100%' }}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Divider>Tab Settings</Divider>
					<Form.List name="tabSettings">
						{(fields, { add, remove }) => (
							<>
								{fields.map(({ key, name, ...restField }) => (
									<Row key={key} gutter={16} align="middle">
										<Col span={6}>
											<Form.Item
												{...restField}
												name={[name, 'rewardType']}
												label="Reward Type"
											>
												<Select disabled>
													<Option value="gifts">Gifts</Option>
													<Option value="giftCards">Gift Cards</Option>
													<Option value="nonMonetary">Non-Monetary</Option>
													<Option value="swag">Swag</Option>
												</Select>
											</Form.Item>
										</Col>
										<Col span={8}>
											<Form.Item
												{...restField}
												name={[name, 'name']}
												label="Display Name"
												rules={[{ required: true, message: 'Required' }]}
											>
												<Input placeholder="Tab display name" />
											</Form.Item>
										</Col>
										<Col span={6}>
											<Form.Item
												{...restField}
												name={[name, 'active']}
												label="Active"
												valuePropName="checked"
											>
												<Switch size="small" />
											</Form.Item>
										</Col>
									</Row>
								))}
							</>
						)}
					</Form.List>

					<Divider>Availability Groups</Divider>
					<div style={{ marginBottom: 16 }}>
						<Button
							type="dashed"
							onClick={() => openAvailabilityGroupModal()}
							icon={<PlusOutlined />}
							style={{ width: '100%' }}
						>
							Add Availability Group
						</Button>
					</div>

					{availabilityGroups.length > 0 && (
						<Table
							dataSource={availabilityGroups}
							rowKey="id"
							pagination={false}
							size="small"
							columns={[
								{
									title: 'Name',
									dataIndex: 'name',
									key: 'name',
								},
								{
									title: 'Description',
									dataIndex: 'description',
									key: 'description',
								},
								{
									title: 'Countries',
									dataIndex: 'countryCodes',
									key: 'countryCodes',
									render: (countryCodes) => (
										<Tag color="red">{countryCodes?.length || 0} countries</Tag>
									),
								},
								{
									title: 'User Groups',
									dataIndex: 'userGroupIds',
									key: 'userGroupIds',
									render: (userGroupIds) => (
										<Tag color="blue">{userGroupIds?.length || 0} groups</Tag>
									),
								},
								{
									title: 'Actions',
									key: 'actions',
									render: (_, record) => (
										<Space>
											<Button
												size="small"
												icon={<EditOutlined />}
												onClick={() => openAvailabilityGroupModal(record)}
											/>
											<Popconfirm
												title="Delete availability group?"
												onConfirm={() =>
													handleDeleteAvailabilityGroup(record.id)
												}
												okText="Yes"
												cancelText="No"
											>
												<Button size="small" danger icon={<DeleteOutlined />} />
											</Popconfirm>
										</Space>
									),
								},
							]}
						/>
					)}

					{/* Availability Group Edit Modal */}
					{editingAvailabilityGroup && (
						<AvailabilityGroupModal
							editingAvailabilityGroup={editingAvailabilityGroup}
							onCancel={() => setEditingAvailabilityGroup(null)}
							onFinish={
								editingAvailabilityGroup.id
									? handleUpdateAvailabilityGroup
									: handleCreateAvailabilityGroup
							}
							userGroups={userGroups}
							countryOptions={countryOptions}
						/>
					)}
				</Form>
			</Modal>

			{/* Snappy API Dev Debugging Modal */}
			<SnappyApiTestModal
				open={snappyApiTestModal}
				onCancel={() => setSnappyApiTestModal(false)}
				client={client}
			/>
		</div>
	);
};

export default RewardsStoreComponent;
